import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { LandingContent } from '../content';
import { Reveal } from '../components/Reveal';
import { SectionContainer } from '../components/SectionContainer';
import { useRevealOnScroll } from '../hooks/useRevealOnScroll';
import { cn } from '../utils';

type LanguageWorldSectionProps = {
  content: LandingContent['languageWorld'];
};

const CHIP_SLOT_COUNT = 9;
const ENTRY_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const MORE_CHIP_REGEX = /^\d+\+/;
const ROTATION_INTERVAL_MS = 3600;
const MOBILE_FIGMA_INITIAL_ORDER: readonly string[] = [
  'Español',
  'English',
  'Français',
  'Deutsch',
  'Čeština',
  'Italiano',
  '中文',
  '日本語',
];

const FLAG_BY_LABEL: Record<string, string> = {
  'Español': '🇪🇸',
  English: '🇬🇧',
  'Français': '🇫🇷',
  Deutsch: '🇩🇪',
  'Čeština': '🇨🇿',
  'Česky': '🇨🇿',
  Italiano: '🇮🇹',
  'हिन्दी': '🇮🇳',
  '中文': '🇨🇳',
  'العربية': '🇸🇦',
  'Português': '🇵🇹',
  '日本語': '🇯🇵',
  '한국어': '🇰🇷',
  Polski: '🇵🇱',
  'Türkçe': '🇹🇷',
  Nederlands: '🇳🇱',
  'Română': '🇷🇴',
  'Українська': '🇺🇦',
  'اردو': '🇵🇰',
  'Ελληνικά': '🇬🇷',
  Svenska: '🇸🇪',
  'Bahasa Indonesia': '🇮🇩',
  'فارسی': '🇮🇷',
  'বাংলা': '🇧🇩',
  'ਪੰਜਾਬੀ': '🇮🇳',
  'தமிழ்': '🇮🇳',
  'Tiếng Việt': '🇻🇳',
  'עברית': '🇮🇱',
  'Bahasa Melayu': '🇲🇾',
  Magyar: '🇭🇺',
  Dansk: '🇩🇰',
};

const LOCALE_TO_LABEL: Record<string, string> = {
  es: 'Español',
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  cs: 'Čeština',
  it: 'Italiano',
  hi: 'हिन्दी',
  zh: '中文',
  ar: 'العربية',
  pt: 'Português',
  ja: '日本語',
  ko: '한국어',
  pl: 'Polski',
  tr: 'Türkçe',
  nl: 'Nederlands',
  ro: 'Română',
  uk: 'Українська',
  ur: 'اردو',
  el: 'Ελληνικά',
  sv: 'Svenska',
  fa: 'العربية',
  id: 'Bahasa Indonesia',
  bn: 'বাংলা',
  pa: 'ਪੰਜਾਬੀ',
  ta: 'தமிழ்',
  vi: 'Tiếng Việt',
  he: 'עברית',
  ms: 'Bahasa Melayu',
  hu: 'Magyar',
  da: 'Dansk',
};

const MOBILE_SLOT_LAYOUT = [
  { left: '5%', top: '0%', rotate: 11, z: 26 },
  { left: '34.4%', top: '7.8%', rotate: -4, z: 27 },
  { left: '65.7%', top: '4.3%', rotate: 1, z: 26 },
  { left: '7%', top: '36.5%', rotate: -12, z: 25 },
  { left: '36.4%', top: '29.8%', rotate: 9, z: 25 },
  { left: '64.1%', top: '27.8%', rotate: 8, z: 25 },
  { left: '6.7%', top: '60%', rotate: 7, z: 24 },
  { left: '36.3%', top: '61.8%', rotate: -6, z: 24 },
  { left: '64.7%', top: '68.9%', rotate: 0, z: 24 },
] as const;

const DESKTOP_SLOT_CLASSES = [
  'rotate-[-12deg] translate-y-2',
  'rotate-[-6deg]',
  'rotate-[-2deg]',
  'rotate-[6deg] -translate-y-1',
  'rotate-[2deg] translate-y-1',
  'rotate-[-10deg] -translate-y-2',
  'rotate-[-4deg]',
  'rotate-[8deg] translate-y-1',
  'rotate-[4deg] -translate-y-1',
] as const;

type SlotTransition = {
  phase: 'out' | 'in';
  direction: 'left' | 'right';
};

function resolvePreferredLanguageLabel(labels: string[]): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const localeCandidates = [
    ...(Array.isArray(window.navigator.languages) ? window.navigator.languages : []),
    window.navigator.language,
  ].filter(Boolean) as string[];

  for (const locale of localeCandidates) {
    const normalized = locale.toLowerCase();
    const base = normalized.split('-')[0];
    const fromFull = LOCALE_TO_LABEL[normalized];
    const fromBase = LOCALE_TO_LABEL[base];
    const candidate = fromFull ?? fromBase;

    if (candidate && labels.includes(candidate)) {
      return candidate;
    }
  }

  return null;
}

export function LanguageWorldSection({ content }: LanguageWorldSectionProps) {
  const { ref: sectionRef, visible } = useRevealOnScroll<HTMLElement>({
    threshold: 0.2,
    rootMargin: '0px 0px -8% 0px',
  });
  const [reducedMotion, setReducedMotion] = useState(false);
  const [hoverGlow, setHoverGlow] = useState({ x: 447, y: 245, active: false });
  const [supportsMapMask, setSupportsMapMask] = useState(false);
  const desktopLayerRef = useRef<HTMLDivElement | null>(null);

  const chipLabels = useMemo(() => content.chips.map((chip) => chip.label), [content.chips]);
  const moreChipLabel = useMemo(
    () => chipLabels.find((label) => MORE_CHIP_REGEX.test(label)) ?? null,
    [chipLabels]
  );
  const rotatingChipLabels = useMemo(
    () => chipLabels.filter((label) => label !== moreChipLabel),
    [chipLabels, moreChipLabel]
  );
  const rotatingSlotCount = moreChipLabel ? CHIP_SLOT_COUNT - 1 : CHIP_SLOT_COUNT;

  const [visibleChipLabels, setVisibleChipLabels] = useState<string[]>(() =>
    chipLabels.slice(0, CHIP_SLOT_COUNT)
  );
  const [slotTransitions, setSlotTransitions] = useState<Record<number, SlotTransition>>({});

  const visibleChipLabelsRef = useRef<string[]>(visibleChipLabels);
  const rotateCursorRef = useRef(0);
  const chipQueueRef = useRef<string[]>([]);
  const priorityLabelRef = useRef<string | null>(null);
  const keepPriorityUntilRef = useRef(0);
  const rotationTimersRef = useRef<number[]>([]);

  useEffect(() => {
    visibleChipLabelsRef.current = visibleChipLabels;
  }, [visibleChipLabels]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyMotion = () => setReducedMotion(motionQuery.matches);
    applyMotion();

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', applyMotion);
      return () => motionQuery.removeEventListener('change', applyMotion);
    }

    motionQuery.addListener(applyMotion);
    return () => motionQuery.removeListener(applyMotion);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.CSS === 'undefined') {
      return;
    }

    const supportsMask =
      window.CSS.supports('mask-image', 'url("/landing/language-world-map.png")') ||
      window.CSS.supports('-webkit-mask-image', 'url("/landing/language-world-map.png")');
    setSupportsMapMask(supportsMask);
  }, []);

  useEffect(() => {
    if (chipLabels.length === 0) {
      setVisibleChipLabels([]);
      setSlotTransitions({});
      chipQueueRef.current = [];
      return;
    }

    const preferred = resolvePreferredLanguageLabel(rotatingChipLabels);
    priorityLabelRef.current =
      preferred && rotatingChipLabels.includes(preferred) ? preferred : null;
    keepPriorityUntilRef.current = Date.now() + 12000;

    const figmaBase = MOBILE_FIGMA_INITIAL_ORDER.filter((label) =>
      rotatingChipLabels.includes(label)
    );
    const missingFromFigma = rotatingChipLabels.filter((label) => !figmaBase.includes(label));
    const initial = [...figmaBase, ...missingFromFigma].slice(0, rotatingSlotCount);
    const nextRotatingVisible =
      preferred && !initial.includes(preferred)
        ? [preferred, ...initial.slice(0, rotatingSlotCount - 1)]
        : initial;
    const nextVisible = moreChipLabel
      ? [...nextRotatingVisible.slice(0, rotatingSlotCount), moreChipLabel]
      : nextRotatingVisible;

    setVisibleChipLabels(nextVisible);
    visibleChipLabelsRef.current = nextVisible;

    const visibleSet = new Set(nextRotatingVisible);
    chipQueueRef.current = rotatingChipLabels.filter((label) => !visibleSet.has(label));
    rotateCursorRef.current = 0;
  }, [chipLabels, moreChipLabel, rotatingChipLabels, rotatingSlotCount]);

  useEffect(() => {
    return () => {
      rotationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      rotationTimersRef.current = [];
    };
  }, []);

  const isVisible = visible || reducedMotion;

  useEffect(() => {
    if (reducedMotion || !isVisible || rotatingChipLabels.length <= rotatingSlotCount) {
      return;
    }

    const pickNextLabel = (currentVisible: string[], currentLabel: string) => {
      if (chipQueueRef.current.length === 0) {
        const visibleSet = new Set(currentVisible.filter((label) => label !== moreChipLabel));
        chipQueueRef.current = rotatingChipLabels.filter((label) => !visibleSet.has(label));
      }

      if (chipQueueRef.current.length === 0) {
        return currentLabel;
      }

      let nextLabel = chipQueueRef.current.shift() as string;
      let guard = 0;
      while (currentVisible.includes(nextLabel) && guard < rotatingChipLabels.length) {
        chipQueueRef.current.push(nextLabel);
        nextLabel = chipQueueRef.current.shift() as string;
        guard += 1;
      }

      chipQueueRef.current.push(currentLabel);
      return nextLabel;
    };

    const interval = window.setInterval(() => {
      const currentVisible = visibleChipLabelsRef.current;
      if (currentVisible.length === 0) {
        return;
      }

      const slotCount = currentVisible.length;
      const fixedMoreIndex =
        moreChipLabel && currentVisible[currentVisible.length - 1] === moreChipLabel
          ? currentVisible.length - 1
          : -1;
      const mutableSlotCount = fixedMoreIndex >= 0 ? slotCount - 1 : slotCount;
      const replaceCount = mutableSlotCount >= 8 ? 3 : mutableSlotCount >= 6 ? 2 : 1;
      const reservedPriority =
        mutableSlotCount > 1 && priorityLabelRef.current && Date.now() < keepPriorityUntilRef.current
          ? 0
          : -1;

      const chosenSlots: number[] = [];
      let cursor = rotateCursorRef.current;
      let guard = 0;

      while (chosenSlots.length < replaceCount && guard < mutableSlotCount * 4) {
        let candidate = cursor % mutableSlotCount;
        cursor += 1;
        guard += 1;

        if (candidate === reservedPriority && mutableSlotCount > 1) {
          candidate = (candidate + 1) % mutableSlotCount;
        }

        if (candidate === fixedMoreIndex) {
          continue;
        }

        if (!chosenSlots.includes(candidate) && candidate < mutableSlotCount) {
          chosenSlots.push(candidate);
        }
      }

      rotateCursorRef.current = cursor;
      if (chosenSlots.length === 0) {
        return;
      }

      const workingVisible = [...currentVisible];
      const planned = chosenSlots
        .map((slotIndex) => {
          const currentLabel = workingVisible[slotIndex] ?? workingVisible[0];
          const nextLabel = pickNextLabel(workingVisible, currentLabel);
          if (!nextLabel || nextLabel === currentLabel) {
            return null;
          }

          const outgoingDirection: 'left' | 'right' = slotIndex % 2 === 0 ? 'left' : 'right';
          const incomingDirection: 'left' | 'right' = outgoingDirection === 'left' ? 'right' : 'left';
          workingVisible[slotIndex] = nextLabel;

          return { slotIndex, nextLabel, outgoingDirection, incomingDirection };
        })
        .filter((item): item is { slotIndex: number; nextLabel: string; outgoingDirection: 'left' | 'right'; incomingDirection: 'left' | 'right' } => Boolean(item));

      if (planned.length === 0) {
        return;
      }

      rotationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      rotationTimersRef.current = [];

      setSlotTransitions((prev) => {
        const next = { ...prev };
        planned.forEach(({ slotIndex, outgoingDirection }) => {
          next[slotIndex] = { phase: 'out', direction: outgoingDirection };
        });
        return next;
      });

      const swapTimer = window.setTimeout(() => {
        setVisibleChipLabels((prev) => {
          const next = [...prev];
          planned.forEach(({ slotIndex, nextLabel }) => {
            next[slotIndex] = nextLabel;
          });
          visibleChipLabelsRef.current = next;
          return next;
        });
        setSlotTransitions((prev) => {
          const next = { ...prev };
          planned.forEach(({ slotIndex, incomingDirection }) => {
            next[slotIndex] = { phase: 'in', direction: incomingDirection };
          });
          return next;
        });
      }, 360);

      const clearTimer = window.setTimeout(() => {
        setSlotTransitions((prev) => {
          const next = { ...prev };
          planned.forEach(({ slotIndex }) => {
            delete next[slotIndex];
          });
          return next;
        });
      }, 920);

      rotationTimersRef.current = [swapTimer, clearTimer];
    }, ROTATION_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
      rotationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      rotationTimersRef.current = [];
    };
  }, [isVisible, moreChipLabel, reducedMotion, rotatingChipLabels, rotatingSlotCount]);

  const revealTransitionStyle = (delayMs: number) =>
    reducedMotion
      ? undefined
      : ({
          transitionDelay: `${delayMs}ms`,
          transitionTimingFunction: ENTRY_EASING,
        } as const);

  const revealClass = cn(
    'transition-[transform,opacity,filter] duration-[900ms]',
    isVisible ? 'translate-y-0 opacity-100 blur-0' : 'translate-y-6 opacity-0 blur-[14px]'
  );

  const mapRevealClass = cn(
    'transition-[transform,opacity,filter] duration-[1100ms]',
    isVisible ? 'scale-100 opacity-[0.9] blur-0' : 'scale-[0.92] opacity-0 blur-[16px]'
  );

  const handleDesktopPointerMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const node = desktopLayerRef.current;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    setHoverGlow({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      active: true,
    });
  };

  const renderChip = (label: string, index: number, isMobile: boolean) => {
    const slotPhase = slotTransitions[index]?.phase ?? null;
    const slotDirection = slotTransitions[index]?.direction ?? null;
    const isMoreChip = MORE_CHIP_REGEX.test(label);
    const mobileLayout = MOBILE_SLOT_LAYOUT[index];

    return (
      <div
        key={`${isMobile ? 'mobile' : 'desktop'}-${index}`}
        className={cn(
          isMobile ? 'absolute' : 'inline-block',
          !isMobile && DESKTOP_SLOT_CLASSES[index],
          !isMobile && 'origin-center'
        )}
        style={
          isMobile
            ? {
                left: mobileLayout.left,
                top: mobileLayout.top,
                transform: `rotate(${mobileLayout.rotate}deg)`,
                zIndex: mobileLayout.z,
              }
            : undefined
        }
      >
        <div
          className={cn(
            isMobile
              ? 'inline-flex items-center gap-2.5 whitespace-nowrap rounded-[999px] border border-[#E7EAF3] bg-white px-3 py-2.5 font-pangram text-[16px] font-semibold leading-[24px] text-[#1C1C1E] shadow-[0_10px_22px_rgba(16,19,29,0.08)] transition-[transform,opacity,filter] duration-[420ms]'
              : 'inline-flex items-center gap-3 rounded-[999px] border border-[#E7EAF3] bg-white px-4 py-2.5 font-pangram text-[16px] font-semibold leading-[20px] text-[#1C1C1E] shadow-[0_10px_22px_rgba(16,19,29,0.08)] transition-[transform,opacity,filter] duration-[420ms]',
            isMobile && isMoreChip && 'pr-4',
            isVisible ? 'translate-y-0 opacity-100 blur-0' : 'translate-y-5 opacity-0 blur-[12px]',
            slotPhase === 'out' &&
              slotDirection === 'left' &&
              'language-chip-out-left',
            slotPhase === 'out' &&
              slotDirection === 'right' &&
              'language-chip-out-right',
            slotPhase === 'in' &&
              slotDirection === 'left' &&
              'language-chip-in-left',
            slotPhase === 'in' &&
              slotDirection === 'right' &&
              'language-chip-in-right'
          )}
          style={revealTransitionStyle(420 + index * 80)}
        >
          {!isMoreChip && (
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#E8EBF3] bg-[#F6F8FF] text-[14px]">
              {FLAG_BY_LABEL[label] ?? '🌐'}
            </span>
          )}
          {isMoreChip ? (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#6195FF] text-white">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
                <path d="m5.25 8 2 2.1 3.5-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : null}
          <span>{label}</span>
        </div>
      </div>
    );
  };

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-[88px]">
      <SectionContainer className="relative z-10 max-w-[1120px]">
        <Reveal variant="up">
          <>
            <div className="relative mx-auto max-w-[895px] sm:hidden">
              <img
                src="/landing/language-world-map.png"
                alt=""
                className={cn(
                  'pointer-events-none absolute left-1/2 top-[92px] w-[398px] -translate-x-1/2',
                  mapRevealClass
                )}
                style={revealTransitionStyle(40)}
                loading="lazy"
                aria-hidden="true"
              />

              <div className="relative z-10 px-2 pb-8 pt-1 text-center">
                <h2
                  className={cn(
                    'mx-auto w-[274px] whitespace-pre-line font-pangaia text-[54px] font-bold leading-[0.9] tracking-[-0.03em] text-[#1C1C1E]',
                    revealClass
                  )}
                  style={revealTransitionStyle(180)}
                >
                  {content.title}
                </h2>
                <p
                  className={cn(
                    'mx-auto mt-5 w-[252px] font-pangram text-[13px] leading-[22px] text-[#1C1C1E]',
                    revealClass
                  )}
                  style={revealTransitionStyle(300)}
                >
                  {content.description}
                </p>

                <div className="relative mx-auto mt-10 h-[208px] w-[402px] max-w-full">
                  {visibleChipLabels.slice(0, CHIP_SLOT_COUNT).map((label, index) =>
                    renderChip(label, index, true)
                  )}
                </div>
              </div>
            </div>

            <div
              ref={desktopLayerRef}
              className="relative mx-auto hidden max-w-[895px] sm:block"
              onMouseMove={handleDesktopPointerMove}
              onMouseEnter={handleDesktopPointerMove}
              onMouseLeave={() => setHoverGlow((prev) => ({ ...prev, active: false }))}
            >
              <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden="true">
                {supportsMapMask ? (
                  <div
                    className={cn(
                      'absolute left-1/2 top-[16px] h-[560px] w-full max-w-[895px] -translate-x-1/2 transition-opacity duration-300',
                      hoverGlow.active ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{
                      background: `radial-gradient(270px 270px at ${hoverGlow.x}px ${Math.max(
                        0,
                        hoverGlow.y - 16
                      )}px, rgba(96,154,255,0.82) 0%, rgba(96,154,255,0.48) 28%, rgba(96,154,255,0.2) 48%, rgba(96,154,255,0.02) 66%, rgba(96,154,255,0) 78%)`,
                      filter: 'blur(6px)',
                      mixBlendMode: 'screen',
                      WebkitMaskImage: 'url(/landing/language-world-map.png)',
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center top',
                      WebkitMaskSize: '100% auto',
                      maskImage: 'url(/landing/language-world-map.png)',
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center top',
                      maskSize: '100% auto',
                    }}
                  />
                ) : (
                  <div
                    className={cn(
                      'absolute h-[252px] w-[252px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-opacity duration-300',
                      hoverGlow.active ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{
                      left: hoverGlow.x,
                      top: hoverGlow.y,
                      background:
                        'radial-gradient(circle, rgba(103,149,255,0.46) 0%, rgba(103,149,255,0.26) 34%, rgba(103,149,255,0) 72%)',
                      filter: 'blur(10px)',
                    }}
                  />
                )}
              </div>

              <img
                src="/landing/language-world-map.png"
                alt=""
                className={cn(
                  'pointer-events-none absolute left-1/2 top-[16px] w-full max-w-[895px] -translate-x-1/2',
                  mapRevealClass
                )}
                style={revealTransitionStyle(40)}
                loading="lazy"
                aria-hidden="true"
              />

              <div className="relative z-10 px-2 pb-10 pt-10 text-center sm:px-8 lg:pt-12">
                <h2
                  className={cn(
                    'mx-auto max-w-[760px] whitespace-pre-line font-pangaia text-[54px] font-bold leading-[0.98] tracking-[-0.035em] text-[#1C1C1E] lg:text-[64px]',
                    revealClass
                  )}
                  style={revealTransitionStyle(180)}
                >
                  {content.title}
                </h2>
                <p
                  className={cn(
                    'mx-auto mt-4 max-w-[468px] font-pangram text-[15px] leading-[24px] text-[#1C1C1E]',
                    revealClass
                  )}
                  style={revealTransitionStyle(300)}
                >
                  {content.description}
                </p>

                <div className="relative mx-auto mt-12 flex max-w-[760px] flex-wrap justify-center gap-x-4 gap-y-4">
                  {visibleChipLabels.slice(0, CHIP_SLOT_COUNT).map((label, index) =>
                    renderChip(label, index, false)
                  )}
                </div>
              </div>
            </div>
          </>
        </Reveal>
      </SectionContainer>
    </section>
  );
}
